import type { RouteLocationRaw } from 'vue-router'

import type { AvailableLocale } from '@/i18n/types'
import type { HbBidderConfig } from '@/modules/adv/header-bidding/types'
import { AdUnitConfig, AdvAction, Creative, OutOfPageAdType, type AdTargeting } from '@/modules/adv/types'
import type { VUE_APP_CONFIG } from '@/modules/config/createConfig'
import type { MessageAction } from '@/modules/main-frame/message-types'
import { AppNativeSDK } from '@/modules/native-app'
import type { SelectedExperimentsData } from '@/server/experiments/types'
import type { PageStatus } from '@/store/app-store'

export enum AdProviderTypes {
    GOOGLE_AD = 'googleAd',
    GOOGLE_INSTREAM = 'googleInstream',
    GOOGLE_AD_SENSE = 'googleAdSense',
    GOOGLE_BANNER = 'googleBanner',
    HEADER_BIDDING_AD = 'headerBiddingAd',

    GAME_DISTRIBUTION = 'gameDistribution',
    EMPTY_PROVIDER = 'emptyProvider',
    APP = 'appProvider',
}

export type GoogleAdName = 'membrana' | 'membrana_vast'

export type TMessageAction = MessageAction & AdvAction

export type GameEvent = MessageEvent<{
    id: string
    action: TMessageAction
    payload?: Record<string, unknown>
    responseToId?: string
    type: 'adv' | 'liveness' | 'error'
}>

export type PaginationType = {
    page: number
    pageSize: number
}

export type PlatformType = 'ios' | 'android' | 'desktop'
export type ClientType = 'mobile-native' | 'mobile-web' | 'desktop-web'

export type APIGameInfoDTO = APIGameInfoType | APIRedirect

export type APIGameInfoType = {
    game: APIGameType
    categoriesInfo: APICategoryShortInfo[]
    pathCategories: APICategoryShortInfo[]
}

export type GameRedirect = {
    type: 'game'
    game_hru: string
    mechanism: string
    reason: string
}

export type APIRedirect = {
    redirectTarget:
        | {
              type: 'main-page'
              mechanism: string
              reason: string
          }
        | {
              type: 'category'
              cat_type: CategoryType
              cat_hru: string
              mechanism: string
              reason: string
          }
        | GameRedirect
    status: 301 | 302
}

export type BadgeType = 'hot' | 'new' | 'top'

export const usefulLinksTypes = [
    'WIKI',
    'APPLE_STORE',
    'GOOGLE_STORE',
    'FANDOM',
    'DISCORD',
    'HUAWEI_STORE',
    'XIAOMI_STORE',
    'SAMSUNG_STORE',
    'AMAZON_STORE',
    'UPTODOWN_STORE',
    'TAPTAP_STORE',
    'OTHER',
]

export type UsefulLinkType = {
    type: (typeof usefulLinksTypes)[number]
    title: string
    url: string
}

export type AgeRating = '0+' | '6+' | '12+' | '16+' | '18+'

export type SmartAdsConfig = {
    version: number
    ads?: {
        rules?: SmartAdsRule[]
        disableFullscreenBanners?: boolean
    }
}

export type SmartAdsRule = {
    type: 'interval'
    data?: {
        intervalScheduleSec?: number[]
        scheduleMode?: string
        skipIfAdShownWithinSec?: number
        skipIfAdShownWithinSecAfter?: number
        request?: string[]
    }
}

export type APIGameMostRatedType = Pick<APIGameType, 'title' | 'rating' | 'preview'> & {
    game_hru: string
}

export type Rating = {
    value: number
    count: number
}

export type APIGameType = {
    id: string
    hru: string
    title: string
    icon: string
    preview: string
    big_preview: string
    description: string
    developer?: string
    developer_id?: string
    how_to_play_text: string
    hasInAppPurchases: boolean
    seo_text: string
    is_hidden: boolean
    is_android: boolean
    is_ios: boolean
    is_desktop: boolean
    main_category: string
    tag: string[]
    categories: string[]
    meta: Array<'recommended' | 'ai_smart_ads' | 'ai_smart_ads-cloud-save' | 'seo_title_experiments'>
    series: string[]
    game_url: string
    engine: string[]
    screenshots: string[]
    links: UsefulLinkType[]
    video_metadata: APIGameVideoMetadataType[]
    videos: APIGameVideoType[]
    vertical: boolean
    config?: SmartAdsConfig
    horizontal: boolean
    source?: string
    rating: Rating
    createdAt: string
    updatedAt: string
    youtube_video: string
    gameUrlRedirectTarget?: GameRedirect
    badges: BadgeType[]
    meta_title?: string
    meta_description?: string
    is_in_export_catalog?: string
    supported_languages?: string[]
    age_rating?: AgeRating
    most_rated?: APIGameMostRatedType
    restricted_countries?: string[]
}

export type GameCategoriesInfoType = {
    categoriesInfo: CategoryShortInfo[]
    pathCategories: CategoryShortInfo[]
}

export type DebugInfoType = unknown & {
    DEBUG: { game_info: Record<string, unknown>; ranking_metrics: Record<string, unknown>[] }
}

export type ShortGameType = {
    hru: string
    preview: string
    icon?: string
    title: string
    videoId?: string
    videoUrl?: string
    hasInAppPurchases?: boolean
    debugInfo?: DebugInfoType
    badges?: BadgeType[]
    source: string
    platforms?: PlatformType[]
}

export type ShortGameTypePlaygamaTop = ShortGameType & {
    fod?: string
    color?: string
    likes?: string
}

export type ShortGameZipType = [
    hru: string,
    title: string,
    preview: string,
    icon: string | undefined,
    videoId: string | undefined,
    videoUrl: string | undefined,
    hasInAppPurchases: boolean,
    rankingDebug: DebugInfoType | undefined,
    badges: BadgeType[],
    source: string,
    platforms: PlatformType[] | undefined,
]

export type APIShortGameType = {
    hru: string
    icon: string
    id: string
    preview: string
    title: string
    videos: APIGameVideoType[]
    video_metadata: APIGameVideoMetadataType[]
    hasInAppPurchases: boolean
    rankingDebug?: { data: DebugInfoType }
    badges: BadgeType[]
    source: string
    platforms?: PlatformType[]
}

export type APIGameVideoType = {
    player_url: string
    preview_url: string
    thumbnail_url: string
}

export type APIGameVideoMetadataType = {
    type: 'gameplay'
    playgama_id: string
    external_url: string
}

export const MAIN_CATEGORIES = [
    'top_playgama',
    'io',
    'action',
    'puzzle',
    'arcade',
    'sports',
    'soccer',
    'strategy',
    'bubble-shooter',
    'girls',
    'boys',
    'car',
    'simulation',
    'shooting',
    'gun',
    'clicker',
    'funny',
    'snake',
    'skill',
    'drawing',
    'idle',
    'multiplayer',
    'solitaire',
    'horror',
    'two-player',
    'trending_now',
    'new',
    'random',
    'obby',
    'top_growth',
] as const

export type MainCategoriesType = (typeof MAIN_CATEGORIES)[number]

export type GameRecommendationType =
    | 'recommended'
    | 'top_playgama'
    | 'related'
    | 'random'
    | 'series'
    | 'linked_categories'

export type APILinkedCategory = {
    category_type: CategoryType
    localizations: [
        {
            lang: AvailableLocale
            title: string
            hru: string
        },
    ]
}

export type LinkedCategory = { hru: string; categoryType: CategoryType; title: string }

export type APIGameRecommendation<T extends GameRecommendationType[]> = {
    [key in T[number]]: (key extends 'linked_categories' ? APILinkedCategory[] : APIShortGameType[]) | undefined
}

export type CategoryType = 'category' | 'tag' | 'series' | 'developer'

export type CategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type NoAdGamConfig = {
    basic: {
        1: number
        7: number
        30: number
    }
}

export type APIGeneralInfo = {
    categories: APICategoryShortInfo[]
    totalGamesCount: number
    noAdsConfigInGam: NoAdGamConfig
}

export type APICategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type APICategoryData = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
    games: APIShortGameType[]
    totalCount: number
}

export type CategoryLocalization = {
    lang: AvailableLocale
    title: string
    id: string
    hru: string
}

export type APICategoryShortInfo = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
    totalCount?: number
    localizations?: CategoryLocalization[]
}

export type CategoryShortInfo = {
    id: string
    hru: string
    title: string
    href: RouteLocationRaw
    categoryType: CategoryType
    totalCount?: number
    localizations?: CategoryLocalization[]
}

export type APICategorySeoInfo =
    | {
          id: string
          hru: string
          seoText: string
          seoPicture?: string
          description: string // full name of categories
          subCategories?: { title: string; id: string; hru: string; categoryType: CategoryType }[]
          pathCategories?: { title: string; id: string; hru: string; categoryType: CategoryType }[]
          metaTitle?: string
          categoryType: CategoryType
          metaDescription?: string
          localizations: {
              lang: AvailableLocale
              hru: string
          }[]
      }
    | APIRedirect

export type CategoryInfo = {
    id: string
    hru: string
    fullTitle: string
    seoText: string
    seoPicture?: string
    metaTitle?: string
    metaDescription?: string
    localizations?: {
        lang: AvailableLocale
        hru: string
    }[]
    subCategories: { title: string; id: string; href: RouteLocationRaw }[]
    pathCategories: { title: string; id: string; href: RouteLocationRaw }[]
}

export type FormSubmitData = {
    message: string
    email: string
    page?: string
    turnstileToken?: string
}

export type UserSocialKeys = 'telegramAcc' | 'tonWallet'
export const SOCIALS = ['telegram', 'ton'] as const
export type SocialsName = (typeof SOCIALS)[number]
export type SocialType = 'account' | 'wallet'

export type UserSocialsType = {
    name: SocialsName
    account?: string
    type: SocialType
    tag: string
    key: UserSocialKeys
    isSupport: boolean
    validate: (value: string) => boolean
}

export type User = {
    _id: string
    firstName: string
    lastName: string
    avatar: string
    fennecs: number
    email?: string
    referralCode: string
    telegramAcc?: string
    tonWallet?: string
    gamesPlayed?: number
    createdAt?: number
    currentStreak?: number
    usersInvited?: number
    goldFenecBalance: number
    adsDisabledUntil?: string
}

export interface GameAttributes {
    clid?: string
    widgetId?: string
    userAcquisitionParams: Record<string, string>
}

export type AdditionalServerData = {
    locale: {
        detectedLocale?: AvailableLocale
    }
    isGooglebot: boolean
    config: VUE_APP_CONFIG
}

export type Currency = 'eur' | 'usd'

export type LinkTarget = '_blank' | '_self'

export type ImportWrapper = <T>(importCallback: () => T) => () => T

export type ServerRenderContext = {
    pageStatus?: PageStatus
    platform: PlatformType
    userAgent?: string
    auid?: string
    countryCode?: string
    rankingParams: RankingParams
    host: string
    referer?: string
    detectedLocale?: AvailableLocale
    experiments: SelectedExperimentsData
    disableCache: boolean
    hasAuthToken: boolean
    contentScope?: string
    landingAttributesCookie?: string
}

export type CustomAdSettings = { initialDelayMs?: number; intervalsMs?: number }

export const RedirectReason = {
    NO_LOCALE_IN_ROUTE: 'no_locale_in_route',
    PREFERRED_LOCALE: 'preferred_locale',
    REMOVE_EN_LOCALE: 'remove_en_locale',
    CATEGORY_TYPE_MISMATCH: 'category_type_mismatch',
    FIRST_CATEGORY_PAGE: 'first_category_page',
    PAGE_OUT_OF_BOUNDS: 'page_out_of_bounds',
    RANDOM_GAME_SET: 'random_game_set',
    SHOP_TO_PROFILE: 'shop_to_profile',
    COUNTRY_RESTRICTED: 'country_restricted',
} as const

export const RedirectMechanism = {
    FRONTEND_ROUTER: 'frontend_router',
    FRONTEND_REDIRECT: 'frontend_redirect',
    FRONTEND_CATEGORY_FALLBACK: 'frontend_category_fallback',
} as const

export type APIRankingInfo = {
    cluster_id: string | null
    audnc: string | null
    found: boolean
    version: string
}

export type RankingParams = { clusterId?: string; audience?: string }

export type CustomAdUnitConfig = {
    platforms: PlatformType[]
    campaignId: string
    creatives: Creative[]
}

export type GoogleAdSettings = {
    type: AdProviderTypes.GOOGLE_AD
    options: {
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        name?: string
        hasRewardedTypeInterstitial?: boolean
        adUnitConfig: AdUnitConfig
        gamAccount: string
        gamSubAccount?: string
        timeout: number
        pageDomain: string
        targeting?: AdTargeting
        platform: PlatformType
    }
}
export type GoogleInstreamSettings = {
    type: AdProviderTypes.GOOGLE_INSTREAM
    options: {
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        adUnitConfig: AdUnitConfig
        gamAccount: string
        gamSubAccount?: string
        timeout: number
        pageURL: string
        targeting?: AdTargeting
        platform: PlatformType
        withoutGptScript?: boolean
        buildAdditionalAdParams?: (type: OutOfPageAdType) => Record<string, string | number>
    }
}

export type GameDistributionsSettings = {
    type: AdProviderTypes.GAME_DISTRIBUTION
    options: {
        targeting?: AdTargeting
        timeout: number
    }
}
export type EmptyProviderSettings = {
    type: AdProviderTypes.EMPTY_PROVIDER
    options: {
        targeting?: AdTargeting
        timeout: number
    }
}
export type AdSenseProviderSettings = {
    type: AdProviderTypes.GOOGLE_AD_SENSE
    options: {
        targeting?: AdTargeting
        timeout: number
        platform: PlatformType
        adUnitConfig: AdUnitConfig
        clientId: string
    }
}

export type AppProviderSettings = {
    type: AdProviderTypes.APP
    options: {
        appNativeSDK?: AppNativeSDK
        platform: PlatformType
        targeting?: AdTargeting
        timeout: number
        adUnitConfig: AdUnitConfig
    }
}

export type GoogleAdBannerSettings = {
    type: AdProviderTypes.GOOGLE_BANNER
    options: {
        headerBidding?: {
            bidders: HbBidderConfig[]
        }
        targeting?: AdTargeting
        timeout: number
        gamAccount: string
        gamSubAccount?: string
        pageDomain: string
        adUnitConfig: AdUnitConfig
        platform: PlatformType
    }
}

export type HeaderBiddingAdSettings = {
    type: AdProviderTypes.HEADER_BIDDING_AD
    options: {
        targeting?: AdTargeting
        timeout: number
        platform: PlatformType
        bidders: HbBidderConfig[]
    }
}

export type AdProvidersSettings =
    | GoogleAdSettings
    | GoogleInstreamSettings
    | GameDistributionsSettings
    | EmptyProviderSettings
    | AdSenseProviderSettings
    | AppProviderSettings
    | GoogleAdBannerSettings
    | HeaderBiddingAdSettings

export type EffectiveType = EffectiveConnectionType | 'not_applicable_ios' | 'not_applicable_other'
